import { isFuture, isPast, subDays } from 'date-fns';
import { SortResult, SortedDocs } from './types';
import { partition } from 'lodash';
import { CoiDuration, DocType, PilotDoc } from '../../../types';
import { sortDocsByDate } from '../../../libs/sortDocsByDate';
import { COI_NOTIFY_IN_DAYS } from '../config';

export function filterAndSortCOI(docs: PilotDoc[], coiDuration?: CoiDuration): SortResult {
  const processedCOI: SortedDocs = {
    good: [],
    needAttention: [],
  };

  // if there is no document of type, return missedDocTypes and stop
  if (docs.length === 0) {
    return { ...processedCOI, missedDocTypes: DocType.CERTIFICATE_OF_INSURANCE };
  }

  const [futureDocs, restDocs] = partition(docs, (d) => d.startDate && isFuture(d.startDate));

  // all future documents should be in good section
  processedCOI.good.push(...futureDocs);

  // if COI duration >= one month and there are no current or expired documents, return future documents, missedDocTypes and stop
  if (restDocs.length === 0 && coiDuration !== CoiDuration.LESS_THAN_ONE_MONTH) {
    return { ...processedCOI, missedDocTypes: DocType.CERTIFICATE_OF_INSURANCE };
  }

  // Note! there could be several current documents, but this case is not covered, since it is rather the data issue
  const currentDoc = restDocs.find((d) => d.startDate && isPast(d.startDate) && d.endDate && isFuture(d.endDate));

  // if COI duration is less one month return all future documents and current one.
  if (coiDuration === CoiDuration.LESS_THAN_ONE_MONTH) {
    if (currentDoc) {
      processedCOI.good.push(currentDoc);
    }

    return processedCOI;
  }

  // if there is a current document, check the expiration period, place it to the proper section, return (current and future) and stop
  // Note! all of the rest (past and probably other current documents will be dropped, even if they marked as 'Fix requested')
  if (currentDoc && currentDoc.endDate) {
    const willExpireSoon = isPast(subDays(currentDoc.endDate, COI_NOTIFY_IN_DAYS));

    if (willExpireSoon) {
      processedCOI.needAttention.push(currentDoc);
    } else {
      processedCOI.good.push(currentDoc);
    }

    return processedCOI;
  }

  // Here there could be only expired documents. Sort them and get one with the latest end date
  const sortedExpiredDocs = sortDocsByDate(restDocs);
  const theLatestExpiredDoc = sortedExpiredDocs[0];

  processedCOI.needAttention.push(theLatestExpiredDoc);

  return processedCOI;
}
